// Number utilities
export const toFloatOrZero = (value) => {
    return Number(value) || 0;
};

export const moneyRound = (amount, decimals = 2) => {
    const scale = Math.pow(10, decimals);
    return Math.round(toFloatOrZero(amount) * scale) / scale;
};

export const moneyRemaining = (total, paid) => {
    return moneyRound(moneyRound(total) - moneyRound(paid));
};

export const resolveSaleTotal = (sale, saleTaxPercent = null) => {
    if (!sale) {
        return 0;
    }

    if (sale.id_on_platform != null) {
        return moneyRound(sale.total ?? 0);
    }

    if (sale.id != null && sale.total != null) {
        return moneyRound(sale.total);
    }

    return calcSaleTotal(sale, saleTaxPercent);
};

export const getCurrencySymbol = () => window.currency_symbol || '$';

/**
 * Locale for number grouping/decimals (e.g. de-DE → 8.745.634,61).
 */
export const getNumberLocale = () => {
    const code = String(window.companyCountryCode || 'us').toLowerCase();

    const localeByCountry = {
        us: 'en-US',
        gb: 'en-GB',
        ca: 'en-CA',
        au: 'en-AU',
        nz: 'en-NZ',
        ie: 'en-IE',
        de: 'de-DE',
        at: 'de-AT',
        ch: 'de-CH',
        nl: 'nl-NL',
        be: 'nl-BE',
        fr: 'fr-FR',
        es: 'es-ES',
        it: 'it-IT',
        pt: 'pt-PT',
        fi: 'fi-FI',
        se: 'sv-SE',
        no: 'nb-NO',
        dk: 'da-DK',
        pl: 'pl-PL',
        cz: 'cs-CZ',
        sk: 'sk-SK',
        hu: 'hu-HU',
        ro: 'ro-RO',
        gr: 'el-GR',
        tr: 'tr-TR',
        am: 'hy-AM',
        ru: 'ru-RU',
        ua: 'uk-UA',
        br: 'pt-BR',
        mx: 'es-MX',
        ar: 'es-AR',
        cl: 'es-CL',
        co: 'es-CO',
        pe: 'es-PE',
        in: 'en-IN',
        jp: 'ja-JP',
        cn: 'zh-CN',
        kr: 'ko-KR',
    };

    if (localeByCountry[code]) {
        return localeByCountry[code];
    }

    // Euro companies without a mapped country still get European grouping.
    if (getCurrencySymbol() === '€') {
        return 'de-DE';
    }

    return 'en-US';
};

/**
 * Format a number with locale-aware thousand separators and decimals.
 * Example (de-DE): 8745634.61 → 8.745.634,61
 */
export const formatNumber = (value, decimals = 2) => {
    const amount = moneyRound(value, decimals === null ? 2 : decimals);
    const places = decimals === null ? 2 : decimals;

    return amount.toLocaleString(getNumberLocale(), {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
        useGrouping: true,
    });
};

export const formatCurrency = (value, decimals = 2) => {
    const symbol = getCurrencySymbol();

    return `${symbol}${formatNumber(value, decimals)}`;
};

export const formatPrice = (value) => {
    return formatCurrency(value);
};

export const calcSaleTotal = (sale, saleTaxPercent = null) => {
    if (!sale) {
        return 0;
    }

    if (sale.id_on_platform != null) {
        return moneyRound(sale.total ?? 0);
    }

    if (sale.subtotal == null) {
        return moneyRound(sale.total ?? 0);
    }

    const defaultTax = (typeof window !== 'undefined' && window.saleTax?.value != null)
        ? window.saleTax.value
        : 0;
    const taxPercent = (sale.tax != null && sale.tax !== '')
        ? toFloatOrZero(sale.tax)
        : (saleTaxPercent ?? defaultTax);
    const subtotal = moneyRound(sale.subtotal);
    const taxAmount = moneyRound(subtotal * toFloatOrZero(taxPercent) / 100);
    let total = moneyRound(subtotal + taxAmount);
    const discountAmount = moneyRound(total * toFloatOrZero(sale.discount) / 100);
    total = moneyRound(total - discountAmount);

    return moneyRound(total + toFloatOrZero(sale.shipping));
};

export const backendErrorString = (error) => {
    const data = error?.response?.data
    if (!data) {
        return error?.message || 'Something went wrong.'
    }

    // Laravel ValidationException: { message, errors: { field: [msg] } }
    if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
        const fieldMessages = Object.values(data.errors)
            .flatMap((messages) => (Array.isArray(messages) ? messages : [messages]))
            .filter((message) => typeof message === 'string' && message.trim() !== '')
        if (fieldMessages.length) {
            return fieldMessages.join('\n')
        }
    }

    // FormRequest HttpResponseException: { field: [msg], ... }
    const directMessages = Object.entries(data)
        .filter(([key]) => key !== 'message')
        .flatMap(([, messages]) => (Array.isArray(messages) ? messages : []))
        .filter((message) => typeof message === 'string' && message.trim() !== '')
    if (directMessages.length) {
        return directMessages.join('\n')
    }

    if (typeof data.message === 'string' && data.message.trim() !== '') {
        return data.message
    }

    return 'Something went wrong.'
};

export const stripHtml = (value) => {
    if (!value) return '';
    // Removes HTML tags
    return value.replace(/<[^>]*>?/gm, '');
}

export const DEFAULT_USER_PICTURE = '/static_images/unisex-avatar.png'

export const normalizeImageUrl = (src) => {
    if (!src || !String(src).trim()) {
        return DEFAULT_USER_PICTURE
    }
    const picture = String(src).trim()
    if (
        picture.includes('/storage/supervisors/0.jpg')
        || picture.endsWith('/static_images/unisex-avatar.png')
        || picture === DEFAULT_USER_PICTURE
    ) {
        return DEFAULT_USER_PICTURE
    }
    if (/^https?:\/\//i.test(picture) || picture.startsWith('//')) {
        return picture
    }
    return `${process.env.VUE_APP_BACKEND_URL}${picture.startsWith('/') ? '' : '/'}${picture}`
}

export const hasCustomUserPicture = (picture) => {
    if (!picture || !String(picture).trim()) {
        return false
    }
    const normalized = String(picture).trim()
    return !normalized.includes('/storage/supervisors/0.jpg')
        && !normalized.includes('/static_images/unisex-avatar.png')
}

export const countryFlagEmoji = (shortName) => {
    const code = String(shortName || '').toUpperCase()
    if (!/^[A-Z]{2}$/.test(code)) {
        return ''
    }
    return String.fromCodePoint(...[...code].map((char) => 127397 + char.charCodeAt(0)))
}

export const normalizeTrackingUrl = (url) => {
    if (!url || !String(url).trim()) {
        return null
    }
    const trimmed = String(url).trim()
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed
    }
    return `https://${trimmed}`
}

export const buildTrackingHref = (trackingUrl, trackingId) => {
    const base = normalizeTrackingUrl(trackingUrl)
    if (!base) {
        return null
    }
    const id = trackingId ? String(trackingId).trim() : ''
    if (!id) {
        return base
    }
    return `${base.replace(/\/+$/, '')}/${id.replace(/^\/+/, '')}`
}

export const resolveAddressCountryId = (address) => {
    if (!address) {
        return null;
    }

    if (address.country_id != null && address.country_id !== '') {
        return address.country_id;
    }

    if (address.country?.id != null) {
        return address.country.id;
    }

    return null;
};

export const getDefaultCustomerAddress = (customer) => {
    const addresses = customer?.addresses || [];

    if (!addresses.length) {
        return null;
    }

    return addresses.find((addr) => addr.default === 1 || addr.default === true) || addresses[0];
};

export const applyCustomerAddressToForm = (form, customer, fallbackCountryId = null) => {
    const address = getDefaultCustomerAddress(customer);

    if (!address) {
        form.customer_address_id = null;
        return;
    }

    form.customer_address_id = address.id ?? null;
    form.customer_address_country_id = resolveAddressCountryId(address) ?? fallbackCountryId ?? null;
    form.customer_address_region_id = address.region_id ?? null;
    form.customer_address_city = address.city ?? null;
    form.customer_address_line_1 = address.address_line_1 ?? null;
    form.customer_address_line_2 = address.address_line_2 ?? null;
    form.customer_address_zip_code = address.zip_code ?? null;
    form.customer_address_name = address.name ?? null;
    form.customer_address_phone = address.phone ?? null;
    form.customer_address_description = address.description ?? null;
};

export const formatCardLast4 = (value) => {
    if (value == null || value === '') {
        return '';
    }

    const digits = String(value).trim();
    if (!digits) {
        return '';
    }

    return `XXXX XXXX XXXX ${digits}`;
};

export const hasCompanyPrintAddress = (company) => {
    if (!company) {
        return false;
    }

    const hasValue = (value) => String(value ?? '').trim() !== '';

    return hasValue(company.address_line_1)
        && hasValue(company.city)
        && hasValue(company.zip_code)
        && (company.country_id != null || hasValue(company.country) || hasValue(company.country_short_name));
};

export const companyPrintDocumentLabel = (printType) => {
    if (printType === 'invoice') {
        return 'invoice';
    }
    if (printType === 'packing_slip') {
        return 'packing slip';
    }
    if (printType === 'delivery_note') {
        return 'delivery note';
    }
    return 'document';
};

export const companyPrintAddressMissingTitle = (printType, action = 'print') => (
    `You can't ${action} the ${companyPrintDocumentLabel(printType)} because your company address is missing`
);

export const COMPANY_PRINT_ADDRESS_MISSING_BODY = 'Please update your company address details on the <a href="/admin/company/settings">Company Settings</a> page.';

// Export all utilities as a single object
const utils = {
    toFloatOrZero,
    moneyRound,
    moneyRemaining,
    calcSaleTotal,
    resolveSaleTotal,
    resolveAddressCountryId,
    getDefaultCustomerAddress,
    applyCustomerAddressToForm,
    getCurrencySymbol,
    getNumberLocale,
    formatNumber,
    formatCurrency,
    formatPrice,
    formatCardLast4,
    backendErrorString,
    stripHtml,
    hasCompanyPrintAddress,
    companyPrintDocumentLabel,
    companyPrintAddressMissingTitle,
    COMPANY_PRINT_ADDRESS_MISSING_BODY,
};

export default utils;
